import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import type { DomainApi } from '@contracts';

/**
 * Provides the single `DomainApi` instance to the component tree
 * (composition root wires the real implementation in src/app/main.tsx per
 * ADR-0001 rule 5). Components never import src/data or src/domain directly
 * — only this context (ADR-0001 rule 4).
 */
const DomainApiCtx = createContext<DomainApi | null>(null);

export function DomainApiProvider({
  api,
  children,
}: {
  readonly api: DomainApi;
  readonly children: ReactNode;
}) {
  return <DomainApiCtx.Provider value={api}>{children}</DomainApiCtx.Provider>;
}

export function useDomainApi(): DomainApi {
  const api = useContext(DomainApiCtx);
  if (!api) {
    throw new Error('useDomainApi must be used within a DomainApiProvider');
  }
  return api;
}

/**
 * Re-renders subscribers on any domain change (DomainApi.subscribe —
 * "fires after any local append, remote integration, or sync-state change").
 * Components pair this with their own query call to re-fetch view models;
 * it does not cache data itself, keeping "no business logic in components"
 * intact — this hook only triggers re-reads, never derives anything.
 */
export function useDomainVersion(): number {
  const api = useDomainApi();
  const [version, setVersion] = useState(0);
  useEffect(() => api.subscribe(() => setVersion((v) => v + 1)), [api]);
  return version;
}
